/* eslint eslint-comments/no-use: off */
/* eslint-disable relay/unused-fields */
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ORGANIZATION_ISSUE_TYPES_LIMIT} from '@github-ui/issue-types/constants.ts'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import type {SharedBulkActionsItemPickerProps} from '@github-ui/item-picker-base/ItemPicker'
import {LazyItemPicker} from '@github-ui/item-picker-base/LazyItemPicker'
import {useItemPickerErrorFallback} from '@github-ui/item-picker-base/useItemPickerErrorFallback'
import {
  IssueTypePickerBase,
  type IssueTypePickerBaseProps,
  type IssueTypePickerIssueType as IssueTypePickerBaseIssueType,
} from '@github-ui/item-picker-issue-type'
import {IssueTypePicker as IssueTypePickerTsq} from '@github-ui/item-picker-issue-type-tsq'
import {clientSideRelayFetchQueryRetained} from '@github-ui/relay-environment'
import {IS_SERVER} from '@github-ui/ssr-utils'
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {type Environment, fetchQuery, graphql, readInlineData, useRelayEnvironment} from 'react-relay'

import type {updateIssueIssueTypeBulkSharedMutation$data} from '../mutations/__generated__/updateIssueIssueTypeBulkSharedMutation.graphql'
import {commitUpdateIssueIssueTypeBulkSharedMutation} from '../mutations/update-issue-issue-type-bulk-shared-mutation'
import type {IssueTypePickerIssueTypeShared$key} from './__generated__/IssueTypePickerIssueTypeShared.graphql'
import type {IssueTypePickerPaginatedShared$key} from './__generated__/IssueTypePickerPaginatedShared.graphql'
import type {IssueTypePickerSharedQuery} from './__generated__/IssueTypePickerSharedQuery.graphql'
import {BulkIssueIssueTypePickerTsqWithRelayWrapper} from './BulkIssueIssueTypePickerTsqWithRelayWrapper'

export const UNSET_ID = 'UNSET'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssueTypeFragment = graphql`
  fragment IssueTypePickerIssueTypeShared on IssueType @inline {
    id
    name
    isEnabled
    description
    color
    pinnedFields {
      __typename
      ... on IssueFieldText {
        id
        name
        dataType
      }
      ... on IssueFieldSingleSelect {
        id
        name
        dataType
      }
      ... on IssueFieldDate {
        id
        name
        dataType
      }
      ... on IssueFieldNumber {
        id
        name
        dataType
      }
    }
  }
`
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssueTypePickerPaginatedFragment = graphql`
  fragment IssueTypePickerPaginatedShared on Repository
  @argumentDefinitions(issueTypesPageSize: {type: "Int!"})
  @inline {
    issueTypes(first: $issueTypesPageSize) @connection(key: "Repository_issueTypes") {
      edges {
        node {
          ...IssueTypePickerIssueTypeShared
        }
      }
      totalCount
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueTypePickerGraphqlSharedQuery = graphql`
  query IssueTypePickerSharedQuery($owner: String!, $repo: String!, $issueTypesPageSize: Int!) {
    repository(owner: $owner, name: $repo) {
      ...IssueTypePickerPaginatedShared @arguments(issueTypesPageSize: $issueTypesPageSize)
    }
  }
`

export async function fetchIssueTypes(
  environment: Environment,
  options: {
    owner: string
    repo: string
    pageSize: number
  },
): Promise<IssueTypePickerIssueType[]> {
  const {pageSize = 10, owner, repo} = options

  const data = await fetchQuery<IssueTypePickerSharedQuery>(
    environment,
    IssueTypePickerGraphqlSharedQuery,
    {
      owner,
      repo,
      issueTypesPageSize: pageSize,
    },
    {fetchPolicy: 'store-or-network'},
  ).toPromise()

  if (!data?.repository) {
    return []
  }

  // eslint-disable-next-line no-restricted-syntax
  const issueTypesData = readInlineData<IssueTypePickerPaginatedShared$key>(
    IssueTypePickerPaginatedFragment,
    data.repository,
  )

  return (issueTypesData?.issueTypes?.edges?.flatMap(a => a?.node) ?? [])
    .filter(node => !!node)
    .map(node => {
      // eslint-disable-next-line no-restricted-syntax
      return readInlineData<IssueTypePickerIssueTypeShared$key>(IssueTypeFragment, node)
    })
}

export type IssueTypePickerTestSharedQueries = {
  IssueTypePickerQuery: IssueTypePickerSharedQuery
}

// We want to keep compat with the existing type
// eslint-disable-next-line relay/no-future-added-value
type IssueFieldDataType = 'DATE' | 'NUMBER' | 'SINGLE_SELECT' | 'TEXT' | '%future added value'

type BaseIssueField = {
  readonly dataType: IssueFieldDataType
  readonly id: string
  readonly name: string
}

type PinnedFieldsType =
  | ReadonlyArray<
      | (BaseIssueField & {readonly __typename: 'IssueFieldDate'})
      | (BaseIssueField & {readonly __typename: 'IssueFieldNumber'})
      | (BaseIssueField & {readonly __typename: 'IssueFieldSingleSelect'})
      | (BaseIssueField & {readonly __typename: 'IssueFieldText'})
      | {
          // This will never be '%other', but we need some
          // value in case none of the concrete values match.
          readonly __typename: '%other'
        }
    >
  | null
  | undefined

export type IssueTypePickerIssueType = IssueTypePickerBaseIssueType & {
  pinnedFields?: PinnedFieldsType
}

export type IssueTypePickerProps = IssueTypePickerBaseProps & {
  repo: string
  owner: string
}

export type IssueTypePickerIssueTypeSharedKey = IssueTypePickerIssueTypeShared$key

export function IssueTypePicker(props: IssueTypePickerProps) {
  const isTsqPickerEnabled = isFeatureEnabled('item_picker_issue_type_tsq_migration')
  return isTsqPickerEnabled ? <IssueTypePickerTsq {...props} /> : <IssueTypePickerRelay {...props} />
}

function IssueTypePickerRelay({anchorElement, ...props}: IssueTypePickerProps) {
  return (
    <LazyItemPicker
      keybindingCommandId="item-pickers:open-issue-type"
      anchorElement={anchorProps => anchorElement(anchorProps)}
      createChild={() => (
        <IssueTypePickerFetcher anchorElement={anchorProps => anchorElement(anchorProps)} {...props} />
      )}
    />
  )
}

function IssueTypePickerFetcher({owner, repo, anchorElement, issueTypes, ...props}: IssueTypePickerProps) {
  const environment = useRelayEnvironment()
  const [isLoading, setIsLoading] = useState(true)
  const [fetchKey, setFetchKey] = useState(0)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<IssueTypePickerPaginatedShared$key | null>(null)

  useEffect(() => {
    if (!IS_SERVER) {
      clientSideRelayFetchQueryRetained<IssueTypePickerSharedQuery>({
        environment,
        query: IssueTypePickerGraphqlSharedQuery,
        variables: {owner, repo, issueTypesPageSize: ORGANIZATION_ISSUE_TYPES_LIMIT},
      }).subscribe({
        next: internalData => {
          setData(internalData.repository ?? null)
          setIsLoading(false)
          setIsError(false)
        },
        error: () => {
          setIsError(true)
          setIsLoading(false)
        },
      })
    }
  }, [environment, fetchKey, owner, repo])

  // eslint-disable-next-line no-restricted-syntax
  const fetchedIssueTypesData = readInlineData<IssueTypePickerPaginatedShared$key>(
    IssueTypePickerPaginatedFragment,
    data,
  )

  const fetchedIssueTypes = useMemo(
    () =>
      fetchedIssueTypesData?.issueTypes?.edges?.flatMap(a =>
        // eslint-disable-next-line no-restricted-syntax
        a?.node ? [readInlineData<IssueTypePickerIssueTypeShared$key>(IssueTypeFragment, a.node)] : [],
      ) || [],
    [fetchedIssueTypesData],
  )

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('types'),
    anchorElement,
    open: true,
  })

  if (isError) {
    return createFallbackComponent(() => setFetchKey(current => current + 1))
  }

  return (
    <IssueTypePickerBase
      isLoading={isLoading}
      anchorElement={anchorElement}
      issueTypes={fetchedIssueTypes}
      {...props}
    />
  )
}

type BulkIssueIssueTypePickerProps = Omit<IssueTypePickerProps, 'onSelectionChange'> & SharedBulkActionsItemPickerProps

export function BulkIssueIssueTypePicker(props: BulkIssueIssueTypePickerProps) {
  const isTsqPickerEnabled = isFeatureEnabled('item_picker_issue_type_tsq_migration')
  return isTsqPickerEnabled ? (
    <BulkIssueIssueTypePickerTsqWithRelayWrapper {...props} />
  ) : (
    <BulkIssueIssueTypePickerRelay {...props} />
  )
}

function BulkIssueIssueTypePickerRelay({
  onError,
  onCompleted,
  issuesToActOn,
  activeIssueType,
  ...rest
}: BulkIssueIssueTypePickerProps) {
  const environment = useRelayEnvironment()
  const {sendAnalyticsEvent} = useAnalytics()
  const onSelectionChange = useCallback(
    (selectedIssueType: IssueTypePickerIssueType[]) => {
      const issueTypeId = selectedIssueType.length === 1 ? selectedIssueType[0]?.id : null
      if (!issueTypeId || issueTypeId === activeIssueType?.id) return
      const mutationArgs = {
        environment,
        onError: (error: Error) => {
          onError?.(error)
        },
      }

      sendAnalyticsEvent('issues_index.bulk_action_issue_type', 'BULK_ISSUE_ISSUE_TYPE_PICKER', {
        initialIssueTypeId: activeIssueType?.id ?? '',
        issueTypeId: issueTypeId === 'UNSET' ? '' : issueTypeId ?? '',
        issueIds: [...issuesToActOn],
      })

      commitUpdateIssueIssueTypeBulkSharedMutation({
        ...mutationArgs,
        input: {
          ids: [...issuesToActOn],
          issueTypeId,
        },
        onCompleted: ({updateIssuesBulk}: updateIssueIssueTypeBulkSharedMutation$data) => {
          onCompleted?.(updateIssuesBulk?.jobId || undefined)
        },
      })
    },
    [activeIssueType?.id, environment, issuesToActOn, onCompleted, onError, sendAnalyticsEvent],
  )
  return <IssueTypePicker {...rest} onSelectionChange={onSelectionChange} showUnset activeIssueType={activeIssueType} />
}

try{ IssueTypePicker.displayName ||= 'IssueTypePicker' } catch {}
try{ IssueTypePickerRelay.displayName ||= 'IssueTypePickerRelay' } catch {}
try{ IssueTypePickerFetcher.displayName ||= 'IssueTypePickerFetcher' } catch {}
try{ BulkIssueIssueTypePicker.displayName ||= 'BulkIssueIssueTypePicker' } catch {}
try{ BulkIssueIssueTypePickerRelay.displayName ||= 'BulkIssueIssueTypePickerRelay' } catch {}